import { useState } from "react"
import LabeledTextarea from "../widgets/LabeledTextarea"
import { FileUpload, SpaceBetween } from "@cloudscape-design/components"
import { recordActivity, upsert, useQueryData } from "../hooks/useQueryData"
import { ConvertDate, NowDB, TodayDB } from "../utils"
import SelectInput from "../widgets/SelectInput"
import YesNo from "../widgets/YesNo"
import { Grid, Stack, Typography } from "@mui/material"
import { saveGCDocumentChunked } from "../services/Documents"
import { CurrentUser } from "../services/SessionService"
import { Money } from "@mui/icons-material"
import MonetaryInput from "../widgets/MonetaryInput"
import SelectCase from "../components/SelectCase"

export const ReceiveDocumentsTaskSettings = {size: "large", title: "Receive Documents Task"}

export default function ReceiveDocumentsTask({values, setMessage, execRef}: {values: any, setMessage: (s: string, t: string) => void, execRef: React.MutableRefObject<() => Promise<string>>}) {
  setMessage("Receive Documents", "info")

  const [ type, setType ] = useState(ReceiveTypes[0])
  const [ treatment, setTreatment ] = useState("")
  const [ createActivity, setCreateActivity ] = useState(true)
  const [ files, setFiles ] = useState<File[]>([])
  const [ notes, setNotes ] = useState('')
  const [ cost, setCost ] = useState('')

  const { data } = useQueryData("Treatment", { Where: [{field: 'CaseId', kind: 'equals', value: values.Case._id}] })
          
  function selectType(t: string) {
    const found = ReceiveTypes.find(rt => rt.name === t)
    if (found) setType(found)
  }

  function newFiles(newFiles: File[]) {
    setFiles(newFiles)
  }

    async function execute(): Promise<string> {
      // Verify we have a case and at least one file
      if (!values?.Case) return "ERROR: No case selected"
      if (files.length === 0) return "ERROR: Please select at least one file to upload"
      // If this is a Completion of Treatment, verify we have a treatment selected
      if (type.name === "Completion of Treatment" && !treatment) return "ERROR: Please select a treatment"

      // Upload files
      setMessage("Uploading files...", "info")
      for (const file of files) {
        const fr = new FileReader()
        fr.onload = async e => {
          const filecontent = e?.target?.result as unknown as Blob
          const result = await saveGCDocumentChunked(values.Case._id, type.path, file.name, filecontent ?? new Blob())
          if (result) {
            const id = await saveDocumentRecord(file.name, 'Upload', result.webUrl, result.embedUrl, result.itemId, e.total ?? 0)
            const desc = 'Document ' + file.name + ' uploaded to ' + type.path + ' for Case ' + values?.Case?.Name
            recordActivity('Add Document', desc, values.Case._id, 'Document', id ?? '')
            // reset()
          } else {
            alert("Failed to save file")
          }
        }
        fr.readAsArrayBuffer(file)
      }

      // Update Treatment if this is a Completion of Treatment and a cost was entered
      if (type.name === "Completion of Treatment" && cost) {
        setMessage("Updating treatment...", "info")
        const t = data?.results?.find((t: any) => treatment.startsWith(t?.Provider?.NameOfProvider + " | " + t?.Provider?.NameOfDoctor))
        if (!t) return "ERROR: Could not find treatment"
        t.Cost = cost
        t.Received = TodayDB()
        const result = await upsert('Treatment', t)
      }

      // Create Activity if requested
      if (type.activity && createActivity) {
        setMessage("Creating activity...", "info")
        const result = await upsert('Activity', {
            ForId: "Case:" + values.Case._id,
            CaseId: values.Case._id,
            Type: type.activity,
            Description: notes,
            On: NowDB(),
            createdBy: CurrentUser().fullName,
        }) 
      }

      return '' // No error. Successful execution
    }

    async function saveDocumentRecord(filename: string, source: string, editURL?: string, embedURL?: string, itemId?: string, fileSize?: number) {        
        const newDocument = {
          createdBy: CurrentUser().fullName,
          CaseId: values.Case._id,
          Source: source,
          Filename: filename,
          EditURL: editURL,
          EmbedURL: embedURL,
          ItemId: itemId,
          FileSize: fileSize,
        }
  
        const id = await upsert('Document', newDocument)
          
        return id
    }

    function textForTreatment(t: any) {
      let status = !t?.Requested ? "Not Requested" : "Requested: " + ConvertDate(t.Requested)
      if (t?.Received) status = "Received: " + ConvertDate(t.Received)
      return t?.Provider?.NameOfProvider + " | " + t?.Provider?.NameOfDoctor + " | " + status
    }

    execRef.current = execute

    return (
      <>
      <SelectCase caseId={values?.Case?._id} name={values?.Case?.Name} onChange={() => {}} />
      <Grid container spacing={2} sx={{marginTop: "8px"}}>
      <Grid item md>
        <Stack direction="column" spacing={2}>
          <SelectInput label="Receiving:" value={type.name} readOnly={false} validate={() => true}      
                onChange={ ({detail}: {detail: {value: string}}) => selectType(detail.value) } 
                options={ReceiveTypes.map(rt => rt.name)} />
          {type?.name === "Completion of Treatment" &&
          <><SelectInput label="Treatment:" value={treatment} readOnly={false} validate={() => true}      
                onChange={ ({detail}: {detail: {value: string}}) => setTreatment(detail.value) } 
                options={data?.results?.map((t: any) => textForTreatment(t))} />
          <Stack direction="row" spacing={2}>
          <MonetaryInput label="Cost" value={cost} onChange={ value => setCost(value) } readOnly={false} />
            <Typography sx={{width: "250px", marginLeft: "8px"}}>* If you enter a cost, we will mark this Treatment as Completed</Typography>
          </Stack></>
          }
          {type.activity && 
          <YesNo label="Would you like to create the Activity completing this?" value={createActivity} readOnly={false} 
            onChange={ b => setCreateActivity(b) }  /> }
          <LabeledTextarea label="Notes" readOnly={false} value={notes} rows={4}
              onChange={ value => setNotes(value) } />
        </Stack>
      </Grid>
      <Grid item md>
        <FileUpload multiple 
            onChange={({ detail }) => newFiles(detail.value)}
            value={files}
            i18nStrings={{
                  uploadButtonText: (e: any) =>
                    e ? "Choose files" : "Choose files",
                  dropzoneText: (e: any) =>
                    e ? "Drop files to upload"
                      : "Drop files to upload",
                  removeFileAriaLabel: (e: any) =>
                    `Remove file ${e + 1}`,
                  errorIconAriaLabel: "Error"
                } as any}
                constraintText="Choose or drop files here"
          />
          <Typography sx={{borderRadius: "10px", backgroundColor: "#E0E0E0", paddingLeft: "8px", paddingRight: "8px", marginTop: "-2px", marginBottom: "-2px", paddingTop: "2px", paddingBottom: "2px"}}>
            <p>Upload <span style={{color: "green"}}>{files.length}</span> file(s) to <span style={{color: "green"}}>{type.path}</span> with the type <span style={{color: "green"}}>{type.docType}</span></p>
            {(type.activity && createActivity) && <p>Create an Activity for <span style={{color: "green"}}>{type.activity}</span></p>}
          </Typography>
      </Grid>
      </Grid>
      </>
    )
}

const ReceiveTypes = [
  {name: "Signed Fee Agreement", path: "Intake/Fee Agreement", docType: "Signed fee agreement", activity: "Fee Agreement Received"},
  {name: "Photo Id", path: "Intake/Photos", docType: "Photo Id", activity: ""},
  {name: "Police Reports", path: "Intake/Accident Report", docType: "Police report", activity: "Police Report Received"},
  {name: "Damage Photos", path: "Property Damage/Photos", docType: "Vehicle damage photos", activity: "Damage Photos Received"},
  {name: "Signed Medical Authorization", path: "Intake/Fee Agreement", docType: "Medical HIPAA authorizations", activity: "Medical Authorization Signed"},
  {name: "Completion of Treatment", path: "Medical and Billing Records/Treatment Records", docType: "Confirmation of Treatment", activity: "Records Received"},
  // {name: "VIS", path: "", docType: "", activity: "VIS Completed"},
  // {name: "SOE", path: "", docType: "", activity: "Statement of Exhibits"},
  // {name: "Demand", path: "", docType: "", activity: "Demand Sent"},
  // {name: "Checks", path: "", docType: "", activity: "Check Received"},
  {name: "Signed Final Accounting", path: "Settlement/Final Accounting", docType: "Final Accounting Signed", activity: "Final Accounting Signed"},
  {name: "Signed Hold Harmless", path: "Settlement/Release", docType: "Hold Harmless", activity: "Hold Harmless Signed"},
]

/*
Fee Agreement Sent
Fee Agreement Received
Police Report Received
Damage Photos Received
Medical Authorization Signed
Intakes Completed
Completed Treatment
Confirmation of Treatment 
Records Ordered
Records Received
VIS Completed
Statement of Exhibits
Demand Sent
Negotiation Call
Case Settled
Check Received
Final Accounting Signed
Hold Harmless Signed
*/

